import { nanoid } from 'nanoid';
import { isNil } from 'lodash';

import {
  getSkipTakeFromPagination,
  paginationRequestSchema,
  getPaginationResponse,
  type PaginationRequest,
  type PaginationResponse,
} from '#packages/common/utils/pagination';
import { type Audit } from '#packages/data-collection/models/audit';

import { useLogger } from '#apps/client-web/app/hooks/use-logger';
import { createLocalDatabase } from '#apps/client-web/app/utils/local-database';

export class AuditRepo {
  protected static readonly logger = useLogger(
    'features/audit/services/audit-repo',
  );

  protected static readonly repo = createLocalDatabase('audits');
  protected static readonly ID_SIZE = 10;

  static async getNewKey(): Promise<string> {
    do {
      const key = nanoid(AuditRepo.ID_SIZE);
      const exists = await AuditRepo.repo.getItem<Audit>(key);

      if (!exists) {
        return key;
      }
    } while (true);
  }

  static async getByKey(auditKey: string): Promise<Audit | undefined> {
    const item = await AuditRepo.repo.getItem<Audit>(auditKey);
    return item ?? undefined;
  }

  static async save({
    key,
    audit,
  }: {
    key: string;
    audit: Audit;
  }): Promise<Audit> {
    return await AuditRepo.repo.setItem<Audit>(key, audit);
  }

  static async list({
    pagination,
  }: {
    pagination?: PaginationRequest;
  }): Promise<[Array<Audit>, PaginationResponse]> {
    const paginationReq = paginationRequestSchema.parse(pagination);
    const { skip, take } = getSkipTakeFromPagination(paginationReq);

    AuditRepo.logger.debug({ pagination: { skip, take } }, 'Listing audits');

    const allKeys = await AuditRepo.repo.keys();
    const totalItems = allKeys.length;

    const selectedKeys = allKeys.slice(skip, skip + take);
    AuditRepo.logger.debug({ selectedKeys }, 'Selected keys for pagination');

    const items: Array<Audit> = [];
    for (const key of selectedKeys) {
      const item = await AuditRepo.repo.getItem<Audit>(key);
      if (isNil(item)) {
        AuditRepo.logger.warn({ key }, 'Audit item is nil');
        continue;
      }

      items.push(item);
    }

    AuditRepo.logger.debug(
      { retrievedItems: items.length, totalItems },
      'Retrieved audits with pagination',
    );

    const paginationRes = getPaginationResponse({
      totalItems,
      pageSize: paginationReq.pageSize,
    });

    return [items, paginationRes];
  }
}
